import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Button, Switch } from '@material-ui/core';
import { EditOutlined } from '@material-ui/icons';
import UserService from '../../../../services/UserService/UserService';
import DataTable from '../../../../components/DataTable/DataTable';
import DeleteConfirm from '../../../../components/DataTable/DeleteConfirm';
import Search from '../../../../components/Search/Search';
import CustomizeView from '../../../../components/DataTable/CustomizeView/CustomizeView';
import Filters from '../../../../components/DataTable/Filters/Filters';
import UserDetails from './UserDetails';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { alertState, rolePermission } from '../../../../recoil-store/recoil-state';
import './ListUsers.css';
import jwtDecode from 'jwt-decode';

let deletedUsers = [];
let setSelectedUserRow;

function ListUserPage(props) {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [searchData, setSearchData] = useState([]);
    const [openCustomizeView, setOpenCustomizeView] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [ownId, setOwnId] = useState();
    const [details, setDetails] = useState({ open: false, id: '' });
    const [customColumn, setCustomColumn] = useState([]);
    const permissions = useRecoilValue(rolePermission)
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        UserService.getAllUsers()
            .then((response) => {
                setData(response.data.data);
                setSearchData(response.data.data)
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
            });
    }
    const handleStatusChange = (id) => (event) => {
        changeStatus(id, event.target.checked);
    };

    const changeStatus = async (id, checked) => {
        const response = await UserService.changeStatus(id, checked);
        if (response) {
            setAlert(alert => ({ ...alert, show: true, message: t(`CONTENT.USER.STATUS-${response.data.message}`), severity: response.data.status ? 'success' : 'error' }));
            if (response.data.status) { fetchData(); };
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
        }
    };

    const handleRowsDelete = useCallback((rowsDeleted, dataArray, setSelectedRows) => {
        setOpenDelete(true);
        deletedUsers = [];
        for (let i of rowsDeleted.data) {
            deletedUsers.push(searchData[i.dataIndex].id);
        }
        setSelectedUserRow = setSelectedRows;
    }, [setSelectedUserRow, setOpenDelete, openDelete, searchData]);


    function handleOpen(open, setState) { setState(!open); }

    const deleteUsers = async () => {
        let ids = deletedUsers;
        setLoadingDelete(true);
        const response = await UserService.deleteUsers(ids);
        if (response) {
            setAlert(alert => ({ ...alert, show: true, message: t(`CONTENT.USER.DELETE-${response.message}`), severity: response.status ? 'success' : 'error' }));
            if (response.status) {
                setSelectedUserRow([]);
                fetchData();
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));

        }
        setLoadingDelete(false);
    };

    useEffect(() => {
        const column = [{
            id: 0,
            name: 'id',
            label: '',
            options: {
                filter: false, sort: false,
                customBodyRenderLite: (dataIndex, rowIndex) => {
                    if (searchData && searchData[dataIndex]) {
                        return <span>{dataIndex + 1}.</span>;
                    }
                }
            }
        }, {
            id: 1,
            name: 'user_name',
            label: t('CONTENT.USER.USERNAME'),
            options: {
                filter: true, sort: true,
                customBodyRenderLite: (dataIndex, rowIndex) => {
                    if (searchData[dataIndex]) {
                        return (
                            <button className="user-detail-link" onClick={() => { setDetails({ open: true, id: searchData[dataIndex]?.id }) }}>
                                {searchData[dataIndex]?.user_name}
                            </button>
                        );
                    }
                }
            }
        }, {
            id: 2,
            name: 'first_name',
            label: t('CONTENT.USER.FIRST-NAME'),
            options: { filter: true, sort: true }
        }, {
            id: 3,
            name: 'second_name',
            label: t('CONTENT.USER.MIDDLE-NAME'),
            options: { filter: true, sort: true, display: false }
        }, {
            id: 4,
            name: 'last_name',
            label: t('CONTENT.USER.LAST-NAME'),
            options: { filter: true, sort: true }
        }, {
            id: 5,
            name: 'user_email',
            label: t('CONTENT.USER.E-MAIL'),
            options: { filter: true, sort: true }
        }, {
            id: 6,
            name: 'status',
            label: t('CONTENT.USER.STATUS'),
            options: {
                filter: false, sort: false,
                customBodyRenderLite: (dataIndex, rowIndex) => {
                    if (searchData[dataIndex]) {
                        return (
                            <Switch
                                checked={searchData[dataIndex]?.status || false}
                                onChange={handleStatusChange(searchData[dataIndex]?.id)}
                                disabled={searchData[dataIndex].id === "9bf95756-269c-478e-8421-8834f19dc359"}
                                size="small" color="primary"
                            />
                        );
                    }
                }
            }
        }, {
            id: 7,
            name: 'actions',
            label: t('CONTENT.CREDENTIAL-TAB.ACTIONS'),
            options: {
                filter: false, sort: false,
                display: permissions?.data.includes('USERMANAGEMENT_UPDATE') ? true : 'excluded',
                customBodyRenderLite: (dataIndex, rowIndex) => {
                    if (searchData[dataIndex] && searchData[dataIndex].id !== "9bf95756-269c-478e-8421-8834f19dc359") {
                        return (
                            <Link to={`/management/user-management/users/${searchData[dataIndex].id}/edit`} style={{ color: 'gray', textDecoration: 'none' }}>
                                <EditOutlined fontSize="small" />
                            </Link>
                        );
                    }
                },
            }
        }]
        handleUserDetails()
        setCustomColumn(column);
    }, [searchData]);

    const handleUserDetails = () => {
        const authToken = localStorage.getItem("authToken")
        const token = JSON.parse(JSON.stringify(authToken))
        const res = jwtDecode(token)
        setOwnId(res.id)
    }

    return (
        <div id="listuser-container" className='data-body-container'>
            <Search data={data} setSearchData={setSearchData} searchKey={['user_name', 'first_name', 'last_name', 'user_email', 'second_name']} xs={11} sm={11} md={11} lg={4} xl={4} spacing={2}>
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                    <Link style={{ textDecoration: 'none' }} to="new">
                        <Button className="searchbar-button">{t('CONTENT.USER.CREATE-USER')}</Button>
                    </Link>
                    <Button className="searchbar-button" onClick={() => { handleOpen(openCustomizeView, setOpenCustomizeView); }}>
                        {t('CONTENT.DATATABLE.TOOLBAR.CUSTOMIZE-VIEW')}
                    </Button>
                    <Button className="searchbar-button" onClick={() => { handleOpen(openFilters, setOpenFilters); }}>
                        {t('CONTENT.DATATABLE.TOOLBAR.FILTERS')}
                    </Button>
                </Grid>
            </Search>
            <DataTable
                customColumns={customColumn} onRowsDelete={handleRowsDelete}
                selectableRows={permissions?.data.includes('USERMANAGEMENT_DELETE') ? 'multiple' : 'none'}
                data={searchData} loading={loading} loadingDelete={loadingDelete} ownId={ownId}
                selectedRowMessage={"CONTENT.USER.SELECTED-MESSAGE"}
                perPageMessage={"CONTENT.USER.PER-PAGE-MESSAGE"}
            />
            {openDelete && <DeleteConfirm setOpenDelete={setOpenDelete} message={t('CONTENT.USER.DELETE-WARNING')} deleteFunc={deleteUsers} />}
            {openCustomizeView && <CustomizeView setOpen={setOpenCustomizeView} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
            {openFilters && <Filters setOpen={setOpenFilters} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
            {details.open && <UserDetails open={details} setOpen={setDetails} />}
        </div>
    );
}

export default ListUserPage;
